import classNames from "classnames";
import React, { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import AssetExportNotificationDetails from "components/asset-management-system/notification-details/AssetExportNotificationDetails";
import AssetRegistrationNotificationDetails from "components/asset-management-system/notification-details/AssetRegistrationNotificationDetails";
import AuditLogDownloadDialog from "components/audit-log/AuditLogDownloadDialog";
import DarkModeSwitch from "components/dark-mode-switch/DarkModeSwitch";
import style from "components/header/header.scss";
import ToggleablePanel from "components/header/ToggleablePanel";
import Bell from "components/icons/Bell";
import Globe from "components/icons/Globe";
import GroupIcon from "components/icons/GroupIcon";
import RefreshButtonIcon from "components/icons/RefreshButtonIcon";
import LanguageMenu from "components/language-menu/LanguageMenu";
import { LicenseAlertData } from "components/licenses/license-alerts/manage-license-alerts/ManageLicenseAlertView";
import { ViewLicenseAlert } from "components/licenses/license-alerts/ViewLicenseAlert";
import LicenseEventExportDownloadDialog from "components/licenses/license-history/LicenseEventExportDownloadDialog";
import Modal from "components/modal/Modal";
import NotificationMenu from "components/notifications/NotificationMenu";
import ReportDeletionNotificationDetails from "components/reports/deletion/ReportDeletionNotificationDetails";
import ReportImportNotificationDetails from "components/reports/import/ReportImportNotificationDetails";
import NotificationDetails from "components/reports/notification-details/NotificationDetails";
import Tooltip from "components/tooltip/Tooltip";
import { UserMenu } from "components/user-menu/UserMenu";
import { getAssetExportTitleKey } from "domain/assetExport";
import { getAssetRegistrationTitleKey } from "domain/assetRegistration";
import { AUTH_LICENSE_VIEW } from "domain/authority";
import { Notification } from "domain/notification";
import * as LanguageRepository from "services/language/languageRepository";
import { Alert, LicenseAlertResult, licenseAlertsService } from "services/licenses/LicenseAlertService";
import { licensePoolService } from "services/licenses/LicensePoolService";
import { LicenseResponse, licenseService } from "services/licenses/LicenseService";
import { notificationService } from "services/notification/NotificationService";
import { ReportDeletionNotificationData } from "services/report/ReportDeletionService";
import { ImportNotificationData } from "services/report/ReportImportService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { getTenantName, hasTenantCookie } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { getInitials, isStringNotBlank, resolveColor } from "utils/commonFunctions";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Panel {
    key: number;
    title: string;
    testId: string;
    children?: ReactElement;
    toggle: boolean;
    icon?: ReactElement;
}

const mapState = (state: StoreState) => {
    const user = state.userReducer.user;
    const username = user === null ? "" : user.username;
    const lang = state.languageReducer.lang;

    return { username, lang };
};

export const useOutsideClick = (callback: () => void): React.RefObject<HTMLDivElement> => {
    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const handleClickOutside = (event: Event) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                callback();
            }
        };

        document.addEventListener("click", handleClickOutside, true);

        return () => {
            document.removeEventListener("click", handleClickOutside, true);
        };
    }, [ref]);

    return ref;
};

const connector = connect(mapState);
const MenuPanel = (props: ConnectedProps<typeof connector>) => {
    const theme = useTheme();
    let username;
    let usernameFormattedForInitials;
    if (getTenantName() != "") {
        username = getTenantName();
    } else {
        username = props.username.split("@")[0];
        usernameFormattedForInitials = username.replace(".", " ");
    }
    const { t } = useTranslation();
    const RETRY_FETCH_NOTIFICATIONS = 15_000;
    const language = props.lang || LanguageRepository.getLanguage();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [notificationVisibility, setNotificationVisibility] = React.useState(false);
    const [notifications, setNotifications] = React.useState<Notification[]>([]);
    const [showBadge, setShowBadge] = React.useState(false);
    const [initialLoading, setInitialLoading] = React.useState(true);

    const fetchNotifications = () => {
        const abortController: AbortController = new AbortController();
        abortControllers.push(abortController);
        notificationService.fetchNotifications(abortController).then((data) => {
            setNotifications(data.sort((a, b) => b.creationDate.localeCompare(a.creationDate)));
        });
    };
    const [reportImportDialogRefreshCount, setReportImportDialogRefreshCount] = React.useState<number>(0);
    const [importNotificationData, setImportNotificationData] = React.useState<ImportNotificationData>({
        jobId: null,
        jobType: "NORMAL",
    });
    const [reportDeletionNotificationData, setReportDeleteNotificationData] =
        React.useState<ReportDeletionNotificationData>({
            jobId: null,
            totalReports: "",
        });
    const [licenseAlertUuid, setLicenseAlertUuid] = React.useState<string>("");
    const [licenseAlertDetails, setLicenseAlertDetails] = React.useState<Alert>({
        alertName: "",
        alertUuid: "",
        tenantUuid: "",
        tenantName: "",
        licenses: [],
        poolName: "",
        poolUuid: "",
    });
    const [licenseAlertDetailsVisibility, setLicenseAlertDetailsVisibility] = React.useState<boolean>(false);
    const [failureMessage, setFailureMessage] = React.useState<string>("");

    const closeReportDeletionResultsDialog = () =>
        setReportDeleteNotificationData((prevState: ReportDeletionNotificationData) => ({
            ...prevState,
            jobId: null,
        }));

    const closeReportImportResultsDialog = () =>
        setImportNotificationData((prevState: ImportNotificationData) => ({
            ...prevState,
            jobId: null,
            totalReports: "",
        }));
    const [reportExportDialogRefreshCount, setReportExportDialogRefreshCount] = React.useState<number>(0);
    const [reportDialogRefreshIconVisibility, setReportDialogRefreshIconVisibility] = React.useState<boolean>(false);
    const [reportExportJobId, setReportExportJobId] = React.useState<string>("");
    const [assetExportDialogRefreshCount, setAssetExportDialogRefreshCount] = React.useState<number>(0);
    const [assetExportDialogRefreshIconVisibility, setAssetExportDialogRefreshIconVisibility] =
        React.useState<boolean>(false);
    const [assetExportJobId, setAssetExportJobId] = React.useState<string>("");
    const [assetRegisterDialogRefreshCount, setAssetRegisterDialogRefreshCount] = React.useState<number>(0);
    const [assetRegisterDialogRefreshIconVisibility, setAssetRegisterDialogRefreshIconVisibility] =
        React.useState<boolean>(false);
    const [assetRegisterJobId, setAssetRegisterJobId] = React.useState<string>("");
    const [auditLogExportData, setAuditLogExportData] = React.useState("");
    const [licenseExportNotification, setLicenseExportNotification] = React.useState<Notification | undefined>();

    const createNotificationBadge = (): JSX.Element | null => {
        const seenUuids = notificationService.getSeenNotifications() ?? [];
        let count = 0;
        for (const index in notifications) {
            const uuid = notifications[index].uuid;
            if (!seenUuids.includes(uuid)) {
                count++;
            }
        }
        if (count == 0) {
            return null;
        }

        return (
            <div
                className={classNames(
                    style.circle,
                    { [style.oneDigitSize]: count < 10 },
                    { [style.twoDigitsSize]: count > 9 && count < 100 },
                    { [style.threeDigitsSize]: count > 99 }
                )}
            >
                <div className={style.count}>{count < 100 ? count : t("Notification.count")}</div>
            </div>
        );
    };

    const handleFetch = () => {
        fetchNotifications();
    };

    React.useEffect(() => {
        if (licenseAlertUuid.length > 0) {
            fetchLicenseData(licenseAlertUuid);
        }
    }, [licenseAlertUuid]);

    const fetchLicenseData = (alertUuid: string) => {
        const abortController = new AbortController();
        const own = !userSessionService.userHasAllAuthorities([AUTH_LICENSE_VIEW]);
        licenseAlertsService
            .fetchLicenseAlertByUuid(alertUuid, abortController)
            .then((result: LicenseAlertResult) => {
                result.alert.poolUuid !== null
                    ? licensePoolService
                          .fetchPoolSpecificLicenses(abortController, result.alert.tenantUuid, result.alert.poolUuid)
                          .then((data) => {
                              const tempTenantsLicenses = data.allLicenses;
                              const processedLicenses: LicenseAlertData[] = result.alert.licenses.map((license) => {
                                  const matchingLicenseData = tempTenantsLicenses.find(
                                      (licenseData) => licenseData.licenseId === license.type
                                  );
                                  return {
                                      amountThreshold: license.amountThreshold,
                                      expirationThreshold: license.expirationThreshold,
                                      triggered: license.triggered,
                                      expirationDate: matchingLicenseData ? matchingLicenseData.expirationDate : "",
                                      remainingLicenses: matchingLicenseData?.amount,
                                      type: license.type,
                                      daysInputVisibility: true,
                                      amountInputVisibility: true,
                                  };
                              });
                              setLicenseAlertDetails({
                                  alertName: result.alert.alertName,
                                  alertUuid: result.alert.alertUuid,
                                  tenantUuid: result.alert.tenantUuid,
                                  tenantName: result.alert.tenantName,
                                  licenses: processedLicenses,
                                  poolName: "",
                                  poolUuid: result.alert.poolUuid,
                              });
                              setLicenseAlertDetailsVisibility(true);
                          })
                          .catch(() => {
                              setFailureMessage(t("LicenseAlerts.requestFailed"));
                              setLicenseAlertDetailsVisibility(true);
                          })
                    : licenseService
                          .fetchLicenses(
                              abortController,
                              undefined,
                              own,
                              result.alert.tenantUuid == userSessionService.currentUserDetails()?.tenantUuid
                                  ? ""
                                  : result.alert.tenantUuid,
                              ""
                          )
                          .then((data: LicenseResponse) => {
                              const tempTenantsLicenses = data.licenses;
                              const processedLicenses: LicenseAlertData[] = result.alert.licenses.map((license) => {
                                  const matchingLicenseData = tempTenantsLicenses.find(
                                      (licenseData) => licenseData.type === license.type
                                  );
                                  return {
                                      amountThreshold: license.amountThreshold,
                                      expirationThreshold: license.expirationThreshold,
                                      triggered: license.triggered,
                                      expirationDate: matchingLicenseData ? matchingLicenseData.expirationDate : "",
                                      remainingLicenses: matchingLicenseData
                                          ? own
                                              ? matchingLicenseData.available
                                              : matchingLicenseData.overallRemaining
                                          : 0,
                                      type: license.type,
                                      daysInputVisibility: true,
                                      amountInputVisibility: true,
                                  };
                              });
                              setLicenseAlertDetails({
                                  alertName: result.alert.alertName,
                                  alertUuid: result.alert.alertUuid,
                                  tenantUuid: result.alert.tenantUuid,
                                  tenantName: result.alert.tenantName,
                                  licenses: processedLicenses,
                                  poolName: result.alert.poolName,
                                  poolUuid: result.alert.poolUuid,
                              });
                              setLicenseAlertDetailsVisibility(true);
                          })
                          .catch(() => {
                              setLicenseAlertDetailsVisibility(true);
                              setFailureMessage("error");
                          });
            })
            .catch(() => {
                setFailureMessage("error");
                setLicenseAlertDetailsVisibility(true);
            });
    };

    const handleClickOutside = () => {
        setNotificationVisibility(false);
    };

    const ref = useOutsideClick(handleClickOutside);

    const getTitle = (notificationType: string) => {
        switch (notificationType) {
            case "REPORT_EXPORT_STARTED":
                return t("Notification.reportNotifications.inProgressTitle");
            case "REPORT_EXPORT_FAILED":
                return t("Notification.reportNotifications.failureTitle");
            case "REPORT_EXPORT_SUCCEEDED":
                return t("Notification.reportNotifications.successTitle");
            case "REPORT_EXPORT_WARNING":
                return t("Notification.reportNotifications.successWithFailTitle");
            default:
                return "";
        }
    };

    React.useEffect(() => {
        if (initialLoading) {
            handleFetch();
            setInitialLoading(false);
            setShowBadge(true);
        }
        const notificationTimer = setTimeout(() => {
            handleFetch();
            setShowBadge(true);
        }, RETRY_FETCH_NOTIFICATIONS);

        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
            clearTimeout(notificationTimer);
        };
    }, [notifications]);

    const menuItems: Panel[] = [
        {
            key: 1,
            title: "darkModeSwitch",
            testId: testIds.navigation.darkModeSwitch.darkModeSwitchButton,
            children: <DarkModeSwitch />,
            toggle: false,
        },
        {
            key: 2,
            title: "notification",
            testId: testIds.navigation.notificationMenu.button,
            children: (
                <NotificationMenu
                    onDelete={setNotifications}
                    notifications={notifications}
                    showImportReportDialog={setImportNotificationData}
                    showExportReport={setReportExportJobId}
                    showExportAsset={setAssetExportJobId}
                    showRegisterAsset={setAssetRegisterJobId}
                    showExportAuditLog={setAuditLogExportData}
                    showExportLicenseEvents={setLicenseExportNotification}
                    showDeleteReportDialog={setReportDeleteNotificationData}
                    showLicenseAlertDialog={setLicenseAlertUuid}
                />
            ),
            toggle: false,
        },
        {
            key: 3,
            title: language.locale,
            testId: testIds.navigation.userMenu.languageMenu.button,
            children: <LanguageMenu />,
            toggle: true,
            icon: <Globe className={style.showOnlyOnMobile} />,
        },
        {
            key: 4,
            title: username,
            testId: testIds.navigation.userMenu.button,
            children: <UserMenu />,
            toggle: true,
            icon: (
                <GroupIcon
                    backgroundColor={theme.whiteColor}
                    textColor={theme.iconFillColor}
                    groupInitials={getInitials(usernameFormattedForInitials ? usernameFormattedForInitials : username)}
                    className={classNames(style.initialsIcon, style.showOnlyOnMobile)}
                />
            ),
        },
    ];

    const setRefreshIconVisibility = (show: boolean) => setReportDialogRefreshIconVisibility(show);
    const notificationData = notifications.filter((each) => each.data.includes(reportExportJobId))[0];
    const assetExportNotificationData = isStringNotBlank(assetExportJobId)
        ? notifications.filter((each) => each.data.includes(assetExportJobId))[0]
        : undefined;
    const assetRegisterNotificationData = isStringNotBlank(assetRegisterJobId)
        ? notifications.filter((each) => each.data.includes(assetRegisterJobId))[0]
        : undefined;
    const hideLicenseAlert = () => {
        setLicenseAlertDetailsVisibility(false);
        setLicenseAlertDetails({
            alertName: "",
            alertUuid: "",
            tenantUuid: "",
            tenantName: "",
            licenses: [],
            poolName: "",
            poolUuid: "",
        });
        setLicenseAlertUuid("");
        setFailureMessage("");
    };

    const reportImportResult = React.useMemo(
        () => (
            <Modal
                isOpen={importNotificationData.jobId !== null}
                hideModal={() =>
                    setImportNotificationData((prevState: ImportNotificationData) => ({ ...prevState, jobId: null }))
                }
                modalTitle={t("Notification.reportImportResultDialog.title")}
                action={
                    reportDialogRefreshIconVisibility && (
                        <Tooltip content={t("Notification.reportNotifications.refreshTableIconTooltip")}>
                            <span
                                className={style.reportImportResultRefreshIcon}
                                onClick={() => {
                                    usageStatisticsService.sendEvent({
                                        category: Category.REPORT_IMPORT,
                                        action: Action.REFRESH,
                                    });
                                    setReportImportDialogRefreshCount((prev) => prev + 1);
                                }}
                            >
                                <span data-testid={testIds.workArea.report.reportResultsDialog.refreshButton}>
                                    <RefreshButtonIcon
                                        color={resolveColor("--app-buttons-buttonPrimary-backgroundColor")}
                                    />
                                </span>
                            </span>
                        </Tooltip>
                    )
                }
            >
                <ReportImportNotificationDetails
                    data={importNotificationData}
                    refreshCount={reportImportDialogRefreshCount}
                    setRefreshIcon={setReportDialogRefreshIconVisibility}
                    onClose={closeReportImportResultsDialog}
                />
            </Modal>
        ),
        [importNotificationData.jobId, reportDialogRefreshIconVisibility, reportImportDialogRefreshCount]
    );

    const reportDeletionResult = React.useMemo(
        () => (
            <Modal
                isOpen={reportDeletionNotificationData.jobId !== null}
                hideModal={() =>
                    setReportDeleteNotificationData((prevState: ReportDeletionNotificationData) => ({
                        ...prevState,
                        jobId: null,
                    }))
                }
            >
                <ReportDeletionNotificationDetails
                    data={reportDeletionNotificationData}
                    onClose={closeReportDeletionResultsDialog}
                />
            </Modal>
        ),
        [reportDeletionNotificationData.jobId]
    );

    const viewLicenseAlert = React.useMemo(
        () => (
            <Modal
                key={1}
                isOpen={licenseAlertDetailsVisibility}
                hideModal={hideLicenseAlert}
                modalTitle={
                    licenseAlertDetails.alertName
                        ? t("LicenseAlerts.viewAlert", { alertName: licenseAlertDetails.alertName })
                        : ""
                }
            >
                {failureMessage !== "" ? (
                    t("LicenseAlerts.requestFailed")
                ) : (
                    <ViewLicenseAlert
                        licenseAlertDetails={licenseAlertDetails}
                        handleOkClick={hideLicenseAlert}
                        showForm={setLicenseAlertDetailsVisibility}
                    />
                )}
            </Modal>
        ),
        [licenseAlertDetailsVisibility]
    );

    return (
        <>
            {menuItems.map((menu) => {
                return (
                    <div onClick={(event) => event.stopPropagation()} className={style.innerMenu} key={menu.key}>
                        {menu.toggle ? (
                            <div className={style.menuItem}>
                                <ToggleablePanel
                                    title={menu.title}
                                    testId={menu.testId}
                                    menuType={"HEADER"}
                                    icon={menu.icon}
                                    buttonClass={style.headerMenuButton}
                                >
                                    {menu.children}
                                </ToggleablePanel>
                            </div>
                        ) : menu.title === "notification" ? (
                            !hasTenantCookie() ? (
                                <div
                                    ref={ref}
                                    className={style.menuItem}
                                    onBlur={(event) => {
                                        if (event.relatedTarget == null) {
                                            return;
                                        }
                                        if (!event.currentTarget.contains(event.relatedTarget)) {
                                            setNotificationVisibility(false);
                                        }
                                    }}
                                    onKeyDown={(event) => {
                                        if (notificationVisibility && event.key === "Escape") {
                                            setNotificationVisibility(false);
                                        }
                                    }}
                                >
                                    <Tooltip content={t("AltText.notification")}>
                                        <button
                                            data-testid={menu.testId}
                                            onClick={() => {
                                                if (notificationVisibility) {
                                                    setNotificationVisibility(false);
                                                } else {
                                                    setNotificationVisibility(true);
                                                    notificationService.setSeenNotifications(notifications);
                                                }
                                            }}
                                            className={style.button}
                                        >
                                            {showBadge && notifications.length > 0 ? createNotificationBadge() : null}
                                            <Bell fillColor={theme.whiteColor} />
                                        </button>
                                    </Tooltip>
                                    {notificationVisibility ? menu.children : <></>}
                                </div>
                            ) : (
                                <></>
                            )
                        ) : (
                            <div data-testid={menu.testId} className={style.showOnlyOnDesktop}>
                                {menu.children}
                            </div>
                        )}
                    </div>
                );
            })}
            {reportImportResult}
            {reportDeletionResult}
            {viewLicenseAlert}
            <Modal
                isOpen={reportExportJobId !== ""}
                hideModal={() => setReportExportJobId("")}
                modalTitle={getTitle(notificationData?.type)}
                action={
                    reportDialogRefreshIconVisibility && (
                        <Tooltip content={t("Notification.reportNotifications.refreshTableIconTooltip")}>
                            <span
                                className={style.reportExportResultRefreshIcon}
                                onClick={() => {
                                    setReportExportDialogRefreshCount((prev) => prev + 1);
                                }}
                            >
                                <RefreshButtonIcon
                                    color={resolveColor("--app-buttons-buttonPrimary-backgroundColor")}
                                />
                            </span>
                        </Tooltip>
                    )
                }
            >
                <div className={style.fixedWidthModal}>
                    <NotificationDetails
                        refreshCount={reportExportDialogRefreshCount}
                        jobId={reportExportJobId}
                        setRefreshIcon={setRefreshIconVisibility}
                        jobDetail={notificationData}
                    />
                </div>
            </Modal>
            <Modal
                isOpen={assetExportJobId !== ""}
                hideModal={() => setAssetExportJobId("")}
                modalTitle={getAssetExportTitleKey(
                    assetExportNotificationData?.type,
                    assetExportNotificationData?.data,
                    t
                )}
                action={
                    assetExportDialogRefreshIconVisibility && (
                        <Tooltip content={t("Notification.assetExportNotifications.refreshTableIconTooltip")}>
                            <span
                                className={style.reportExportResultRefreshIcon}
                                onClick={() => {
                                    setAssetExportDialogRefreshCount((prev) => prev + 1);
                                }}
                            >
                                <RefreshButtonIcon
                                    color={resolveColor("--app-buttons-buttonPrimary-backgroundColor")}
                                />
                            </span>
                        </Tooltip>
                    )
                }
            >
                <div className={style.fixedWidthModal}>
                    <AssetExportNotificationDetails
                        refreshCount={assetExportDialogRefreshCount}
                        setRefreshIcon={setAssetExportDialogRefreshIconVisibility}
                        jobDetail={assetExportNotificationData}
                    />
                </div>
            </Modal>
            <Modal
                isOpen={assetRegisterJobId !== ""}
                hideModal={() => setAssetRegisterJobId("")}
                modalTitle={getAssetRegistrationTitleKey(assetRegisterNotificationData?.type, t)}
                action={
                    assetRegisterDialogRefreshIconVisibility && (
                        <Tooltip content={t("Notification.assetRegistrationNotifications.refreshTableIconTooltip")}>
                            <span
                                className={style.reportExportResultRefreshIcon}
                                onClick={() => {
                                    setAssetRegisterDialogRefreshCount((prev) => prev + 1);
                                }}
                            >
                                <RefreshButtonIcon
                                    color={resolveColor("--app-buttons-buttonPrimary-backgroundColor")}
                                />
                            </span>
                        </Tooltip>
                    )
                }
            >
                <div className={style.fixedWidthModal}>
                    <AssetRegistrationNotificationDetails
                        refreshCount={assetRegisterDialogRefreshCount}
                        setRefreshIcon={setAssetRegisterDialogRefreshIconVisibility}
                        jobDetail={assetRegisterNotificationData}
                    />
                </div>
            </Modal>
            <Modal
                isOpen={auditLogExportData !== ""}
                hideModal={() => setAuditLogExportData("")}
                modalTitle={t("AuditLogs.export.downloadTitle")}
            >
                {auditLogExportData && <AuditLogDownloadDialog notificationData={auditLogExportData} />}
            </Modal>
            <Modal
                isOpen={licenseExportNotification !== undefined}
                hideModal={() => setLicenseExportNotification(undefined)}
                modalTitle={t("LicenseEvent.export.downloadTitle")}
            >
                {licenseExportNotification && (
                    <LicenseEventExportDownloadDialog notification={licenseExportNotification} />
                )}
            </Modal>
        </>
    );
};

export default connector(MenuPanel);
